import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Statement } from '../models/statement/statement.model';

@Injectable({
    providedIn: 'root'
})
export class FormHelperService {

    validationMessages = {
        'contactFirstName': {
            'required': 'Merchant First Name is required.',
        },
        'contactLastName': {
            'required': 'Merchant Last Name is required.',
        },
        'contactEmail': {
            'required': 'Merchant Email is required.',
            'email': 'Invalid email format.',
            'invalid': 'Invalid email address.',
        },
        'contactPhone': {
            'pattern': 'Invalid phone number format.'
        },
        'companyName': {
            'required': 'Company Name is required.'
        },
        'statementsPerMonth': {
            'required': 'Field is required.',
            'min': 'Must be greater than or equal to 1'
        },
        'totalEmployees': {
            'required': 'Field is required.',
            'min': 'Must be greater than or equal to 1'
        },
        'userFirstName': {
            'required': 'User First Name is required.',
        },
        'userLastName': {
            'required': 'User Last Name is required.',
        },
        'userEmail': {
            'required': 'User Email is required.',
            'email': 'Invalid email format.',
            'invalid': 'Invalid email address.',
            'emailDuplicate': 'Email has already been used.'
        },
        'firstName': {
            'required': 'First Name is required.',
            'minlength': 'First Name must be at least 3 characters.',
            'maxlength': 'First Name should not exceed 100 characters.',
            'pattern': 'First Name should not start or end with spaces.'
        },
        'lastName': {
            'required': 'Last Name is required.',
            'minlength': 'Last Name must be at least 3 characters.',
            'maxlength': 'Last Name should not exceed 100 characters.',
            'pattern': 'Last Name should not start or end with spaces.'
        },
        'email': {
            'required': 'Email is required.',
            'email': 'Invalid email format.',
            'invalid': 'Invalid email address.',
            'emailDuplicate': 'Email has already been used.',
            'exists': 'Email already used.'
        },
        'phone': {
            'required': 'Phone Number is required.',
            'pattern': 'Invalid phone number format.'
        },
        'confirmPassword': {
            'equalTo': 'Passwords do not match!'
        },
        'feeLabel': {
            'required': 'Fee Label is required.',
        },
        'volume': {
            'required': 'Volume is required.',
        },
        'feeDescription': {
            'required': 'Fee Description is required.',
        },
        'fees': {
            'required': 'Fees are required.',
        },
        'numberOfTransactions': {
            'required': 'Number of Transactions is required.',
        },
        'organizationName': {
            'required': 'Organization Name is required.',
        },
        
    };

    defaultTimeZone = {
        name: 'America/Chicago',
        viewValue: '(GMT-6:00) America/Chicago'
    };

    constructor(
    private fb: UntypedFormBuilder
    ) {

    }

    timeZoneValidator(control: AbstractControl) {
        return typeof control.value === 'object' ? null : { 'badTimeZone': true };
    }

    displayFn(value: any): string {
        return value && typeof value === 'object' ? value.name : value;
    }

    createStmtEditForm(statementModel: Statement, isReview: boolean): UntypedFormGroup {
        const processorFeeGrouping = statementModel.feeGroupings.processor;
        const additionalFeeGrouping = statementModel.feeGroupings.additional;
        const assessmentFeeGrouping = statementModel.feeGroupings.assessment;
        const nonNegotiableFeeGrouping = statementModel.feeGroupings.nonNegotiable;
        const interchangeFeeGrouping = statementModel.feeGroupings.interchange;

        const procFees = processorFeeGrouping.fees.map(f => {
            return this.createProcFeeFormGroup(f);
        });
        const additFees = additionalFeeGrouping.fees.map(f => {
            return this.createAdditOrAssessFeeFormGroup(f);
        });
        const assessFees = assessmentFeeGrouping.fees.map(f => {
            return this.createAdditOrAssessFeeFormGroup(f);
        });
        const nonNegotFees = nonNegotiableFeeGrouping.fees.map(f => {
            return this.createNonNegotiableFeeFormGroup(f);
        });
        const icFees = interchangeFeeGrouping.fees.map(f => {
            return this.createInterchangeFeeFormGroup(f);
        });

        let nameCtrl;
        if (isReview) {
            nameCtrl = this.fb.control({ value: statementModel.friendlyName, disabled: true });
        } else {
            nameCtrl = this.fb.control(statementModel.friendlyName, Validators.required);
        }
        // tODOlater MMD - stmtEditForm Validators
        const stmtEditForm = this.fb.group({
            friendlyName: nameCtrl,
            totalGrossSales: [statementModel.totalGrossSales],
            structure: [statementModel.structure, [Validators.required]],
            notes: statementModel.notes,
            processorFees: this.fb.array(procFees),
            additionalFees: this.fb.array(additFees),
            assessmentFees: this.fb.array(assessFees),
            nonNegotiableFees: this.fb.array(nonNegotFees),
            interchangeFees: this.fb.array(icFees)
        });

        return stmtEditForm;
    }

    createProcFeeFormGroup(procFee: any): UntypedFormGroup {
    // todolater mmd - proc fee validators
        const procFeeDetails = procFee.feeDetails.map(fd => {
            if (fd.transactionRate) {
                fd.transRatePercent = Number((fd.transactionRate * 100).toFixed(6));
            } else {
                fd.transactionRate = 0;
                fd.transRatePercent = 0;
            }
            return this.fb.group({
                description: [fd.description, [Validators.required]],
                amount: [fd.amount],
                volume: [fd.volume, [Validators.required]],
                transactionRate: [fd.transactionRate],
                transRatePercent: [fd.transRatePercent, [Validators.required]],
                transactionFee: [fd.transactionFee],
                monthlyTransactions: [fd.monthlyTransactions]
            });
        });
        return this.fb.group({
            label: [procFee.label, [Validators.required]],
            totalAmount: [procFee.totalAmount],
            totalTransactions: [procFee.totalTransactions],
            totalVolume: [procFee.totalVolume],
            feeDetails: this.fb.array(procFeeDetails),
            _id: [procFee.id]
        });
    }

    createBlankProcFeeFormGroup(): UntypedFormGroup {
    // todolater mmd - proc fee validators
        const procFeeDetails = [];
        procFeeDetails.push(this.createProcFeeDetailFormGroup());
        return this.fb.group({
            label: ['New Fee', [Validators.required]],
            totalAmount: [0],
            totalTransactions: [0],
            totalVolume: [0],
            feeDetails: this.fb.array(procFeeDetails),
            _id: [null]
        });
    }

    createProcFeeDetailFormGroup() {
        const fg = this.fb.group({
            description: ['New Fee Detail', [Validators.required]],
            // amount: [0],
            volume: [null, [Validators.required]],
            transactionRate: [0],
            transRatePercent: [0, [Validators.required]],
            transactionFee: [null],
            monthlyTransactions: [null]
        });
        fg.get('volume').markAsDirty();
        fg.get('volume').markAsTouched();
        return fg;
    }

    // for ADDITIONAL and ASSESSMENT fees
    createAdditOrAssessFeeFormGroup(fee?: any): UntypedFormGroup {
    // todolater mmd - other fee validators
        let fg;
        if (fee) {
            fg = this.fb.group({
                label: [fee.label, [Validators.required]],
                amount: [fee.amount, [Validators.required]],
                _id: [fee.id]
            });
        } else {
            fg = this.fb.group({
                label: ['New Fee', [Validators.required]],
                amount: [null, [Validators.required]],
                _id: [null]
            });
            fg.get('amount').markAsDirty();
            fg.get('amount').markAsTouched();
        }
        return fg;
    }

    createNonNegotiableFeeFormGroup(fee?: any): UntypedFormGroup {
    // todolater mmd - other fee validators
        let fg: UntypedFormGroup;
        if (fee) {
            fg = this.fb.group({
                label: [fee.label, [Validators.required]],
                amount: [fee.amount, [Validators.required]],
                isFlagged: [fee.isFlagged || false],
                _id: [fee.id]
            });
        } else {
            fg = this.fb.group({
                label: ['New Fee', [Validators.required]],
                amount: [null, [Validators.required]],
                isFlagged: [false],
                _id: [null]
            });
            fg.get('amount').markAsDirty();
            fg.get('amount').markAsTouched();
        }
        return fg;
    }

    createInterchangeFeeFormGroup(fee?: any): UntypedFormGroup {
    // todolater mmd - other fee validators
        let fg;
        if (fee) {
            fg = this.fb.group({
                label: [fee.label, [Validators.required]],
                amount: [fee.amount, [Validators.required]],
                isFlagged: [fee.isFlagged || false],
                isPadded: [fee.isPadded || false],
                trueAmount: [fee.trueAmount || null],
                _id: [fee.id]
            });
        } else {
            fg = this.fb.group({
                label: ['New Fee', [Validators.required]],
                amount: [null, [Validators.required]],
                isFlagged: [false],
                isPadded: [false],
                trueAmount: [null],
                _id: [null]
            });
            fg.get('amount').markAsDirty();
            fg.get('amount').markAsTouched();
        }
        return fg;
    }
}
